export default function Terms() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="bg-amber-50 border-l-4 border-amber-400 p-6 mb-8">
          <div className="flex">
            <div className="ml-3">
              <p className="text-lg text-amber-700 font-semibold">
                For Healthcare Professionals Only
              </p>
              <p className="text-amber-600 mt-1">
                GPMeta is designed exclusively for qualified healthcare professionals.
                Not for patient use or clinical decision-making.
              </p>
            </div>
          </div>
        </div>

        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>

        <div className="prose max-w-none">
          <p className="text-gray-600 mb-8">
            Governing law: South Australia, Australia.
          </p>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Who We Are</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              GPMeta (gpmeta.au) is operated by Phronesis Health Pty Ltd (ABN: 52 658 820 449)
              ("we", "us", "GPMeta"). Founded by Dr James Condon, Adelaide, South Australia.
            </p>
            <p className="text-gray-700">Contact: hello@gpmeta.au</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Who Can Use GPMeta</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              GPMeta is for registered Australian healthcare professionals only.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">By creating an account you confirm:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>You are a registered healthcare professional in Australia</li>
              <li>You will use GPMeta only in a professional capacity</li>
              <li>You will not share your account with patients or non-HCPs</li>
              <li>You are at least 18 years old</li>
            </ul>
            <p className="text-gray-700">
              We reserve the right to terminate accounts where these conditions are not met.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">What GPMeta Is</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              GPMeta is an information synthesis tool. It searches and summarises publicly
              available Australian clinical resources in response to plain-English queries.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-4">
              <p className="text-gray-700 leading-relaxed mb-2 font-semibold">GPMeta is not:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>A medical device</li>
                <li>A clinical decision support system under TGA definitions</li>
                <li>A substitute for clinical judgement</li>
                <li>A provider of medical advice</li>
              </ul>
            </div>
            <p className="text-gray-700 font-semibold">
              All clinical decisions remain your professional responsibility.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Account</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>You are responsible for maintaining account security</li>
              <li>Do not share login credentials</li>
              <li>Notify us immediately of unauthorised access: hello@gpmeta.au</li>
              <li>One account per individual — not transferable</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Subscriptions and Billing</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Trial</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>60-day free trial on signup — no credit card required</li>
              <li>Trial ends automatically at day 60</li>
              <li>No automatic charge at trial end — you choose to upgrade</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Paid Plans</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Billed monthly via Stripe</li>
              <li>Pricing displayed at gpmeta.au/pricing</li>
              <li>GST included in all Australian prices</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Early Adopter Plan</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>$19/month AUD — lifetime pricing for qualifying early users</li>
              <li>Price will not increase while subscription remains active and continuous</li>
              <li>Cancellation terminates lifetime pricing — reinstatement at current standard rate</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Cancellation</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Cancel anytime via Settings → Billing</li>
              <li>No lock-in, no cancellation fees</li>
              <li>Access continues to end of current billing period</li>
              <li>No refunds for partial months</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Changes to Pricing</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Standard tier pricing may change with 30 days notice</li>
              <li>Early adopter lifetime pricing is exempt from increases</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acceptable Use</h2>
            <p className="text-gray-700 leading-relaxed mb-4">You must not use GPMeta to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Query on behalf of patients without professional context</li>
              <li>Attempt to extract, scrape, or reproduce source content at scale</li>
              <li>Reverse engineer, copy, or resell the service</li>
              <li>Submit false or misleading information</li>
              <li>Circumvent rate limits or access controls</li>
              <li>Use the demo widget with automated tools or bots</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              GPMeta's software, design, and prompting architecture are owned by
              Phronesis Health Pty Ltd. Source content belongs to respective third parties
              and is accessed under their terms.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You retain ownership of queries you submit. You grant us a limited licence
              to process queries to deliver the service and improve it (anonymised only —
              see <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>).
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Service Availability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We aim for high availability but do not guarantee uninterrupted service.
              Planned maintenance will be notified via hello@gpmeta.au where possible.
              No refunds for downtime unless unreasonable and sustained.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Sources</h2>
            <p className="text-gray-700 leading-relaxed">
              GPMeta retrieves information from third-party sources. We are not responsible
              for the accuracy, currency, or availability of those sources. Always verify
              against the primary source before clinical application.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To the maximum extent permitted by the Australian Consumer Law:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>We are not liable for clinical outcomes arising from use of GPMeta</li>
              <li>Our total liability for any claim is capped at fees paid in the prior 3 months</li>
              <li>We are not liable for indirect, consequential, or special damages</li>
            </ul>
            <p className="text-gray-700">
              Nothing in these terms excludes rights under the Australian Consumer Law
              that cannot be excluded.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Your use of GPMeta is also governed by our <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>.
              We do not store individual patient data. Query content is processed to deliver
              responses and is not linked to patient records.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Termination</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We may suspend or terminate accounts for:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Breach of these terms</li>
              <li>Non-payment after grace period</li>
              <li>Fraudulent or abusive use</li>
            </ul>
            <p className="text-gray-700">You may terminate at any time via Settings.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These terms are governed by the laws of South Australia, Australia.
              Disputes subject to the jurisdiction of South Australian courts.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to These Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update these terms. You will be notified by email 14 days before
              material changes take effect. Continued use constitutes acceptance.
            </p>
          </section>

          <div className="border-t border-gray-200 pt-8 mt-12">
            <p className="text-sm text-gray-500 mb-2">
              Last updated: April 2026<br />
              Version: 1.0
            </p>
          </div>
        </div>

        <div className="mt-16 text-center">
          <a
            href="/"
            className="text-blue-600 hover:underline mr-8"
          >
            Back to GPMeta
          </a>
          <a
            href="/privacy"
            className="text-blue-600 hover:underline"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  )
}